import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ArrDataGridPro from '../../../common/arr-data-grid-pro/ArrDataGridPro';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import NoJobs from './NoJobs';
import {
  GridAlignment,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import FilterTextField from '../../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import './JobList.scss';
import {columnsData, IColumnsData, tableProps} from './utils';
import {useNavigate} from 'react-router-dom';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrMenu, {ArrMenuHandle} from '../../../common/arr-menu/ArrMenu';
import ArrModal, {ModalButton} from '../../../common/arr-modal/ArrModal';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ReactComponent as MoreIcon} from '../../../../assets/images/icon-more.svg';
import {IAIReviewJobGroupsResponse} from '../../../../api/types/aiReview.type';
import {IAIReviewJobsExportRequest} from '../../../../api/types/aiReview.type';
import {
  useAddJobToGroup,
  useDeleteAIReviewJob,
  useRemoveJobFromGroup,
  useRetryAIReviewJob,
  useGetJobList,
  useGetAIReviewJobDetailData,
} from '../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../shared/constants';
import JobListActionMenuItem from './JobListActionMenuItem';
import {
  AIReviewJobStatus,
  getJobListMenuItems,
  parseJobListRowStatus,
  exportAIReviewJobDetailsPdf,
} from './utils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {IJobGroup} from '../utils';
import CellWrapper from '../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';

interface IJobList {
  reviewJobGroupsResponse?: IAIReviewJobGroupsResponse;
  onExportPayloadChange?: (payload: IAIReviewJobsExportRequest) => void;
  onHasJobsChange?: (hasJobs: boolean) => void;
  setIsCreateJob: React.Dispatch<React.SetStateAction<boolean>>;
  selectedGroup: IJobGroup | null;
}

/** Set when opening the kebab menu; cleared on menu close. Fixes menu items using stale React state. */
type MenuAnchorRow = {
  status: AIReviewJobStatus | null;
  groupName: string | null;
  jobName: string | null;
  jobId: string | number;
};

type JobActionToastState = {
  isOpen: boolean;
  message: string;
  status: ToastType;
};

const defaultJobActionToastState: JobActionToastState = {
  isOpen: false,
  message: '',
  status: ToastType.Success,
};

const cellText = (
  text: string,
  className: string,
  dataTestId: string,
  cellParams: any,
  formatFunction?: (...args: string[]) => void
): React.ReactElement => {
  const formattedText = formatFunction?.(text, cellParams.row.status) ?? text;
  return (
    <CellWrapper
      toolTipContent={formattedText ?? ''}
      dataTestId={`tooltip-cell-content-${cellParams.id} `}
      cellParams={cellParams}
    >
      <Typography
        className={`cell-content typo-text body3_size ${className}`}
        data-testid={dataTestId ?? `job-list-typography`}
      >
        {formattedText ?? '---'}
      </Typography>
    </CellWrapper>
  );
};

const JobList = (props: IJobList) => {
  const {onExportPayloadChange, onHasJobsChange} = props;
  const navigate = useNavigate();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel | null>(null);

  const [pageSize, setPageSize] = useState<number>(
    tableProps.DEFAULT_PAGE_SIZE
  );
  const [pageNumber, setPageNumber] = useState<number>(0);

  const menuItemsRef = useRef<ArrMenuHandle | null>(null);
  const menuAnchorRowRef = useRef<MenuAnchorRow | null>(null);

  const [selectedRowStatus, setSelectedRowStatus] =
    useState<AIReviewJobStatus | null>(null);
  const [selectedRowJobName, setSelectedRowJobName] = useState<string | null>(
    null
  );
  const [selectedRowGroupName, setSelectedRowGroupName] = useState<
    string | null
  >(null);
  const [selectedRowJobId, setSelectedRowJobId] = useState<string | null>(null);
  const [selectedMenuGroupName, setSelectedMenuGroupName] = useState<
    string | null
  >(null);
  const [isAddToGroupExpanded, setIsAddToGroupExpanded] =
    useState<boolean>(false);
  const [groupSearchValue, setGroupSearchValue] = useState<string>('');
  const [appliedGroupSearch, setAppliedGroupSearch] = useState<string>('');
  const [groupSearchPerformed, setGroupSearchPerformed] =
    useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDeleteJobId, setPendingDeleteJobId] = useState<
    string | number | null
  >(null);
  const [pendingDeleteJobName, setPendingDeleteJobName] = useState<string>('');
  const [jobActionToast, setJobActionToast] = useState<JobActionToastState>(
    defaultJobActionToastState
  );
  const [hasJobListLoadError, setHasJobListLoadError] = useState(false);
  const {mutateAsync: getAIReviewJobDetailData} = useGetAIReviewJobDetailData();
  const {mutateAsync: deleteJob} = useDeleteAIReviewJob();
  const {mutateAsync: retryJob} = useRetryAIReviewJob();
  const {mutateAsync: addJobToGroup} = useAddJobToGroup();
  const {mutateAsync: removeJobFromGroup} = useRemoveJobFromGroup();

  const openJobActionToast = useCallback(
    (message: string, status: ToastType) => {
      setJobActionToast({
        isOpen: true,
        message,
        status,
      });
    },
    []
  );

  const {
    mutate: getJobList,
    data: jobsData,
    isPending: isLoadingJobs,
  } = useGetJobList(
    (response) => {
      const hasValidResponse = !!response && Array.isArray(response.jobs);
      setHasJobListLoadError(!hasValidResponse);
    },
    () => {
      setHasJobListLoadError(true);
    }
  );

  const availableGroups = useMemo(
    () =>
      (props.reviewJobGroupsResponse?.jobGroups ?? []).map((group) => ({
        id: String(group.jobGroupId),
        group_name: group.name,
      })),
    [props.reviewJobGroupsResponse]
  );

  const handleActionsMenuClose = useCallback(() => {
    menuAnchorRowRef.current = null;
  }, []);

  const handleMoreIconClick = (
    e: React.MouseEvent<HTMLElement>,
    rowStatus: AIReviewJobStatus | null,
    rowGroupName: string | null,
    rowJobName: string | null,
    rowJobId: string
  ) => {
    menuAnchorRowRef.current = {
      status: rowStatus,
      groupName: rowGroupName,
      jobName: rowJobName,
      jobId: rowJobId,
    };
    setSelectedRowStatus(rowStatus);
    setSelectedRowJobName(rowJobName);
    setSelectedRowGroupName(rowGroupName);
    setSelectedRowJobId(rowJobId);
    setSelectedMenuGroupName(null);
    setIsAddToGroupExpanded(false);
    setGroupSearchValue('');
    setAppliedGroupSearch('');
    setGroupSearchPerformed(false);
    menuItemsRef.current?.open(e.currentTarget as HTMLElement);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setPendingDeleteJobId(null);
    setPendingDeleteJobName('');
  };

  const handleConfirmDeleteJob = async () => {
    const jobId = pendingDeleteJobId;
    const jobName = pendingDeleteJobName;
    if (!jobId) {
      handleCloseDeleteModal();
      return;
    }
    try {
      await deleteJob({jobId});
      fetchJobList();
      openJobActionToast(
        t('ai.review.jobList.jobListActions.deleteJobSuccess', {
          jobName: jobName ?? '',
        }),
        ToastType.Success
      );
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.deleteJobFailed', {
          jobName: jobName ?? '',
        }),
        ToastType.Error
      );
    }
    setIsDeleteModalOpen(false);
    setPendingDeleteJobId(null);
    setPendingDeleteJobName('');
  };

  const handleExportJobDetailsAsPdf = async (jobId: string | number | null) => {
    if (!jobId) return;

    try {
      const getJsonData = await getAIReviewJobDetailData({jobId});
      exportAIReviewJobDetailsPdf(getJsonData);
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.noRecordFoundForJobId'),
        ToastType.Error
      );
    }
  };

  const handleRetryJob = async (
    jobId: string | number | null,
    jobName: string | null
  ) => {
    if (!jobId) return;
    try {
      await retryJob({jobId});
      fetchJobList();
      openJobActionToast(
        t('ai.review.jobList.jobListActions.retrySuccess', {
          jobName: jobName ?? '',
        }),
        ToastType.Success
      );
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.retryFailed', {
          jobName: jobName ?? '',
        }),
        ToastType.Error
      );
    }
  };

  const handleAddJobToGroup = async (groupId: string, groupName: string) => {
    const jobId = menuAnchorRowRef.current?.jobId ?? selectedRowJobId;
    const jobName = menuAnchorRowRef.current?.jobName ?? selectedRowJobName;
    if (!jobId) return;
    const currentAssignedGroupName =
      menuAnchorRowRef.current?.groupName ??
      selectedMenuGroupName ??
      selectedRowGroupName;

    if (currentAssignedGroupName === groupName) {
      setSelectedMenuGroupName(groupName);
      setSelectedRowGroupName(groupName);
      setIsAddToGroupExpanded(false);
      return;
    }

    try {
      if (currentAssignedGroupName) {
        const currentGroup = availableGroups.find(
          (group) => group.group_name === currentAssignedGroupName
        );
        if (currentGroup) {
          await removeJobFromGroup({
            groupId: currentGroup.id,
            jobId: String(jobId),
          });
        }
      }
      await addJobToGroup({
        groupId,
        jobId: String(jobId),
      });
      setSelectedMenuGroupName(groupName);
      setSelectedRowGroupName(groupName);
      setIsAddToGroupExpanded(false);
      fetchJobList();
      openJobActionToast(
        t('ai.review.jobList.jobListActions.addToGroupSuccess', {
          jobName: jobName ?? '',
          groupName,
        }),
        ToastType.Success
      );
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.addToGroupFailed', {
          jobName: jobName ?? '',
          groupName,
        }),
        ToastType.Error
      );
    }
  };

  const handleRemoveJobFromGroup = async (groupName: string | null) => {
    const jobId = menuAnchorRowRef.current?.jobId ?? selectedRowJobId;
    const jobName = menuAnchorRowRef.current?.jobName ?? selectedRowJobName;
    const groupToRemove = groupName ?? selectedRowGroupName;
    if (!jobId || !groupToRemove) return;
    const targetGroup = availableGroups.find(
      (group) => group.group_name === groupToRemove
    );
    if (!targetGroup) return;
    try {
      await removeJobFromGroup({
        groupId: targetGroup.id,
        jobId: String(jobId),
      });
      setSelectedMenuGroupName(null);
      setSelectedRowGroupName(null);
      fetchJobList();
      openJobActionToast(
        t('ai.review.jobList.jobListActions.removeFromGroupSuccess', {
          jobName: jobName ?? '',
          groupName: groupToRemove,
        }),
        ToastType.Success
      );
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.removeFromGroupFailed', {
          jobName: jobName ?? '',
          groupName: groupToRemove,
        }),
        ToastType.Error
      );
    }
  };

  const visibleGroups = useMemo(() => {
    const query = appliedGroupSearch.trim().toLowerCase();
    if (!query) return availableGroups;
    return availableGroups.filter((g) =>
      g.group_name.toLowerCase().includes(query)
    );
  }, [appliedGroupSearch, availableGroups]);

  const handleGroupSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGroupSearchValue(e.target.value);
  };

  const handleGroupSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      const trimmed = groupSearchValue.trim();
      setAppliedGroupSearch(trimmed);
      setGroupSearchPerformed(trimmed.length > 0);
    }
  };

  const handleGroupSearchClear = () => {
    setGroupSearchValue('');
    setAppliedGroupSearch('');
    setGroupSearchPerformed(false);
  };

  useEffect(() => {
    if (!isAddToGroupExpanded) {
      handleGroupSearchClear();
    }
  }, [isAddToGroupExpanded]);

  const effectiveRowStatus =
    menuAnchorRowRef.current?.status ?? selectedRowStatus;
  const effectiveGroupName =
    menuAnchorRowRef.current?.groupName ?? selectedRowGroupName;
  const effectiveJobName =
    menuAnchorRowRef.current?.jobName ?? selectedRowJobName;
  const effectiveJobId = menuAnchorRowRef.current?.jobId ?? selectedRowJobId;

  const menuItems = getJobListMenuItems(effectiveRowStatus);
  const hasActionsMenu = menuItems.length > 0;

  const exportPayload = useMemo<IAIReviewJobsExportRequest>(() => {
    const filters: IAIReviewJobsExportRequest['filters'] = {};
    filterModel.items.forEach((item) => {
      if (!item?.field || item.value == null) return;
      const value = String(item.value).trim();
      if (!value) return;
      if (item.field === 'job_name') filters.jobName = value;
      if (item.field === 'status') filters.status = value;
      if (
        item.field === 'createdDateFrom' ||
        item.field === 'created_date_from'
      )
        filters.createdDateFrom = value;
      if (item.field === 'createdDateTo' || item.field === 'created_date_to')
        filters.createdDateTo = value;
    });

    const activeSort = sortModel?.[0];
    const sortFieldRaw = activeSort?.field ?? 'created_date';
    const getSortField = (field: string) => {
      switch (field) {
        case 'created_date':
          return 'createdDate';
        case 'executed_date':
          return 'executedDate';
        case 'job_name':
          return 'jobName';
        case 'created_by':
          return 'createdBy';
        default:
          return field;
      }
    };
    const sortField = getSortField(sortFieldRaw);

    return {
      filters,
      sort: {
        field: sortField,
        direction: activeSort?.sort === 'asc' ? 'ASC' : 'DESC',
      },
      pagination: {
        page: pageNumber || 1,
        pageSize,
      },
    };
  }, [filterModel, sortModel, pageNumber, pageSize]);

  useEffect(() => {
    onExportPayloadChange?.(exportPayload);
  }, [exportPayload, onExportPayloadChange]);

  useEffect(() => {
    onHasJobsChange?.((jobsData?.jobs?.length ?? 0) > 0);
  }, [jobsData, onHasJobsChange]);

  const getColumns = (): GridColDef[] => {
    const columns: GridColDef[] = [
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 48,
        minWidth: 48,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          const rowStatus = parseJobListRowStatus(params.row?.status);
          const rowGroupName = (params.row?.jobGroupName ?? null) as
            | string
            | null;
          const rowJobName = (params.row?.jobName ?? null) as string | null;
          const rowJobId = params?.row?.jobId;
          return (
            <ArrIconButton
              onClick={(e) =>
                handleMoreIconClick(
                  e,
                  rowStatus,
                  rowGroupName,
                  rowJobName,
                  rowJobId
                )
              }
              icon={<MoreIcon />}
              ariaLabel="action"
              shape="none"
              testId={`user-more-action-btn-${params.id}`}
              className="job-list-icon-button"
            />
          );
        },
      },
    ];

    columnsData.forEach((col: IColumnsData) => {
      columns.push({
        field: col.field,
        headerName: col.headerName,
        filterable: col.filterable,
        sortable: col.sortable,
        width: col.width,
        minWidth: col.width,
        align: (col?.align as GridAlignment) ?? 'left',
        renderHeader: (params) => (
          <Typography
            className="typo-text label2_size"
            tabIndex={0}
            data-testid={`job-list-typography-text`}
          >
            {params.colDef.headerName}
          </Typography>
        ),
        renderCell: (param: any) => {
          const colorClass =
            param.row[col.field] === null
              ? 'text-body-subtle'
              : (col.textColorClass[
                  param.row?.status?.toLowerCase() as AIReviewJobStatus
                ] ?? 'text-headings');
          return (
            <>
              {col?.clickable &&
              !!col?.clickable[
                param.row.status.toLowerCase() as AIReviewJobStatus
              ] ? (
                <ArrButton
                  onClick={() => {
                    param.row.status.toLowerCase() === 'draft'
                      ? navigate(
                          `/AI/Review/CreateJob?jobId=${param.row.jobId}`
                        )
                      : navigate('/AI/Review/JobResults', {state: param.row});
                  }}
                  variant="text"
                  size="xs"
                  className="id-cell-preview"
                  dataTestId={`job-list-button-${param.id}`}
                >
                  {cellText(
                    param.row[col.fieldDataKey],
                    colorClass,
                    `button-${col.headerName}-${param.id}`,
                    param,
                    col.formatFunction
                  )}
                </ArrButton>
              ) : (
                cellText(
                  param.row[col.fieldDataKey],
                  colorClass,
                  `text-${col.headerName}-${param.id}`,
                  param,
                  col.formatFunction
                )
              )}
            </>
          );
        },
        renderHeaderFilter: (params) => {
          return (
            <>
              {col.filterable && !!jobsData?.jobs && (
                <FilterTextField
                  dataTestId={col.headerName}
                  gridRenderHeaderFilterProps={params}
                  placeholder={t('common.filter')}
                  className="custom-filter"
                />
              )}
            </>
          );
        },
      });
    });

    return columns;
  };

  // just for demo purpose, will remove later
  useEffect(() => {
    setPageNumber(1);
  }, []);

  useEffect(() => {
    if (props.selectedGroup) {
      setPageNumber(1);
      setPageSize(tableProps.DEFAULT_PAGE_SIZE);
      setSortModel([{field: 'ReviewJobID', sort: 'desc'}]);
      setFilterModel({
        items: [],
      });
    }
  }, [props.selectedGroup]);

  useEffect(() => {
    if (pageNumber < 1) return;
    if (!sortModel) {
      setSortModel([{field: 'ReviewJobID', sort: 'desc'}]);
      return;
    } else if (sortModel.length === 0) {
      return;
    }
    fetchJobList();
  }, [pageNumber, pageSize, sortModel, filterModel]);

  function fetchJobList() {
    getJobList({
      sortingColumn: sortModel?.[0]?.field ?? 'ReviewJobID',
      direction: sortModel?.[0]?.sort ?? 'desc',
      displayLength: pageSize, // keep -1 for all the rows
      pageNumber: pageNumber,
      columnFilter: [
        ...filterModel.items.map((item) => ({
          columnName: item.field,
          value: item.value,
        })),
        ...(props.selectedGroup
          ? [
              {
                columnName: 'JobGroupId',
                value: props.selectedGroup.jobGroupId.toString(),
              },
            ]
          : []),
      ],
    });
  }

  const handleRetryLoadJobs = () => {
    setHasJobListLoadError(false);
    fetchJobList();
  };

  const renderJobListErrorOverlay = () => (
    <Box
      className="job-list-error-overlay"
      data-testid="job-list-error-overlay"
    >
      <Typography
        className="typo-text subhead1_size text-headings job-list-error-title"
        data-testid="job-list-error-title"
      >
        {t('ai.review.jobList.jobApiFailureTitle')}
      </Typography>
      <Typography
        className="typo-text body2_size text-body-subtle job-list-error-description"
        data-testid="job-list-error-description"
      >
        {t('ai.review.jobList.jobApiFailureDescription')}
      </Typography>
      <ArrButton
        variant="primary"
        size="medium"
        dataTestId="job-list-error-refresh-button"
        onClick={handleRetryLoadJobs}
      >
        {t('common.refresh')}
      </ArrButton>
    </Box>
  );

  return (
    <Box className="job-list">
      <ArrDataGridPro
        slotProps={{
          cell: {
            tabIndex: 0,
          },
        }}
        rowHeight={tableProps.ROW_HEIGHT}
        data-testid="ai-job-list-data-grid"
        columns={getColumns()}
        columnHeaderHeight={48}
        rows={hasJobListLoadError ? [] : (jobsData?.jobs ?? [])}
        rowCount={hasJobListLoadError ? 0 : jobsData?.totalCount}
        noRowsOverlay={
          hasJobListLoadError
            ? () => renderJobListErrorOverlay()
            : !jobsData?.jobs
              ? () => <NoJobs addJobs={() => props.setIsCreateJob(true)} />
              : undefined
        }
        getRowId={(row: any) => row.jobId}
        showPerPageOptions
        itemsPerPage={pageSize}
        setItemsPerPage={setPageSize}
        currentPage={pageNumber}
        onPageChange={setPageNumber}
        pageSizeOptions={tableProps.PAGE_SIZE_OPTIONS}
        pagination
        disableColumnResize={false}
        sortModel={sortModel ?? []}
        onSortModelChange={setSortModel}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        headerFilters={!!jobsData}
        headerFilterHeight={tableProps.FILTER_HEADER_HEIGHT}
        disableColumnReorder
        showCellVerticalBorder
        disableVirtualization
        entityType={t('common.entries')}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        disableColumnFilter
        loading={isLoadingJobs}
        loadingOverlay={() => <ArrLoadingSpinner />}
        alwaysShowPagination
      />
      <ArrModal
        descriptionHasHtml={true}
        showModal={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        showClose={false}
        title={t('ai.review.jobList.deleteJobModal.title')}
        ariaLabel={t('ai.review.jobList.deleteJobModal.title')}
        description={t('ai.review.jobList.deleteJobModal.description', {
          jobName: pendingDeleteJobName ?? '',
        })}
        ariaDescription={t('ai.review.jobList.deleteJobModal.description', {
          jobName: pendingDeleteJobName ?? '',
        })}
        dataTestId="job-list-modal"
        buttons={[
          {
            id: 'cancel-delete-job',
            label: t('ai.review.jobList.deleteJobModal.cancelBtn'),
            onClick: handleCloseDeleteModal,
            variant: 'secondary',
            dataTestId: 'cancel-delete-job-button',
          } as ModalButton,
          {
            id: 'confirm-delete-job',
            label: t('ai.review.jobList.deleteJobModal.deleteBtn'),
            onClick: handleConfirmDeleteJob,
            variant: 'danger',
            dataTestId: 'confirm-delete-job-button',
          } as ModalButton,
        ]}
      />
      <ArrMenu
        id="action-menu"
        ref={menuItemsRef}
        items={
          hasActionsMenu ? menuItems : [{id: 'noop', label: '', value: 'noop'}]
        }
        onItemClick={() => {}}
        onClose={handleActionsMenuClose}
        direction="right"
        className="job-list__actions-menu"
        keepOpenOnItemClick={true}
        autoFocusMenu={true}
        disableAutoFocusItem={false}
        renderItem={(item, helpers) => {
          return (
            <JobListActionMenuItem
              item={item}
              helpers={helpers}
              selectedRowStatus={effectiveRowStatus}
              selectedRowGroupName={effectiveGroupName}
              selectedMenuGroupName={selectedMenuGroupName}
              selectedRowJobName={effectiveJobName}
              selectedRowJobId={effectiveJobId}
              isAddToGroupExpanded={isAddToGroupExpanded}
              groupSearchValue={groupSearchValue}
              groupSearchPerformed={groupSearchPerformed}
              visibleGroups={visibleGroups}
              onToggleAddToGroup={() =>
                setIsAddToGroupExpanded((prev) => !prev)
              }
              onGroupSearchChange={handleGroupSearchChange}
              onGroupSearchKeyDown={handleGroupSearchKeyDown}
              onGroupSearchClear={handleGroupSearchClear}
              onAddToGroup={handleAddJobToGroup}
              onRemoveFromGroup={handleRemoveJobFromGroup}
              onOpenDeleteJob={(jobName, jobId) => {
                setPendingDeleteJobName(jobName);
                setPendingDeleteJobId(jobId);
                setIsDeleteModalOpen(true);
              }}
              onRetryJob={handleRetryJob}
              onExportJobDetailsAsPdf={handleExportJobDetailsAsPdf}
            />
          );
        }}
      />
      {jobActionToast.isOpen && (
        <ArrToastNotification
          message={jobActionToast.message}
          status={[jobActionToast.status]}
          onClose={() => setJobActionToast(defaultJobActionToastState)}
        />
      )}
    </Box>
  );
};

export default JobList;
